// components/EnquiryModalPopup.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import EnquiryModal from "../components/contact-us/EnquiryModal";

const propertyTypes = ["Residential", "Commercial", "Industrial"];

const EnquiryModalPopup = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
    propertyType: "",
    location: "",
    message: "",
  });
  const [properties, setProperties] = useState([]); // ✅ state for property list
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // ✅ Fetch projects once (not inside handleSubmit)
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/projects`);
        const data = await res.json();
        console.log("Projects API response:", data);

        let projectArray = [];
        if (Array.isArray(data)) {
          projectArray = data;
        } else if (Array.isArray(data.projects)) {
          projectArray = data.projects;
        } else if (Array.isArray(data.data)) {
          projectArray = data.data;
        } else {
          console.error("Unexpected API response format:", data);
        }

        // ✅ Filter Jaipur projects
        projectArray = projectArray.filter(
          (project) =>
            project.city?.toLowerCase() === "jaipur" ||
            project.location?.toLowerCase().includes("jaipur") ||
            project.address?.toLowerCase().includes("jaipur")
        );

        // ✅ Sort alphabetically
        projectArray.sort((a, b) => (a.name || "").localeCompare(b.name || ""));

        // ✅ Save directly (no grouping needed for dropdown)
        setProperties(projectArray);
      } catch (err) {
        console.error("Error fetching properties:", err);
        setProperties([]);
      }
    };

    fetchProperties();
  }, []);

  const validateEmail = (email) => /^\S+@\S+\.\S+$/.test(email);
  const validatePhone = (phone) => /^\d{10}$/.test(phone);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone" && !/^\d*$/.test(value)) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePropertyTypeClick = (type) => {
    setFormData((prev) => ({ ...prev, propertyType: type }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { fullName, phone, email, propertyType } = formData;

    if (!fullName || !phone || !email || !propertyType) {
      setError("Please fill all required fields.");
      setSuccess("");
      return;
    }

    if (!validateEmail(email)) {
      setError("Invalid email format.");
      return;
    }

    if (!validatePhone(phone)) {
      setError("Phone must be 10 digits.");
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: formData.fullName,
          phone: formData.phone,
          email: formData.email,
          property_type: formData.propertyType,
          location: formData.location,
          message: formData.message,
        }),
      });

      if (response.ok) {
        setSuccess("Your enquiry has been submitted!");
        setError("");
        setFormData({
          fullName: "",
          phone: "",
          email: "",
          propertyType: "",
          location: "",
          message: "",
        });
        setTimeout(() => {
          setSubmitting(false);
          onClose();
           navigate("/thank-you"); 
        }, 1500);
      } else {
        const data = await response.json();
        setError(data?.message || "Submission failed.");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to submit. Try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <EnquiryModal isOpen={isOpen} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Full Name */}
        <div className="flex items-end gap-5">
          <label className="text-sm w-20 whitespace-nowrap">Full Name*</label>
          <input
            type="text"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            className="w-full border-b border-gray-400 focus:outline-none focus:border-black py-1"
          />
        </div>

        {/* Phone */}
        <div className="flex items-end gap-5">
          <label className="text-sm w-20">Phone*</label>
          <input
            type="text"
            name="phone"
            maxLength={10}
            value={formData.phone}
            onChange={handleChange}
            className="w-full border-b border-gray-400 focus:outline-none focus:border-black py-1"
          />
        </div>

        {/* Email */}
        <div className="flex items-end gap-5">
          <label className="text-sm w-20">Email*</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="w-full border-b border-gray-400 focus:outline-none focus:border-black py-1"
          />
        </div>


                {/* Property Name Dropdown */}
       <div className="flex items-end gap-5 !mb-4">
  <label className="text-sm w-32 mb-0">Property Name*</label>
  <select
    name="location"
    value={formData.location}
    onChange={handleChange}
    className="w-full border-b border-gray-400 focus:outline-none focus:border-black py-1"
    required
  >
    <option value="">-- Select Property --</option>

    {Object.values(
      properties.reduce((acc, p) => {
        if (!acc[p.name]) {
          acc[p.name] = p; // keep first instance
        }
        return acc;
      }, {})
    )
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
      .map((p) => (
        <option key={p.slug} value={p.name}>
          {p.name}
        </option>
      ))}
  </select>
</div>

        {/* Property Type */}
        <div className="flex items-center gap-5 mt-5 mb-2">
          <label className="text-sm w-32">Property Type*</label>
          <div className="flex flex-wrap gap-2">
            {propertyTypes.map((type) => (
              <button
                type="button"
                key={type}
                className={`px-4 py-1.5 border rounded-full text-sm ${
                  formData.propertyType === type
                    ? "border-black font-semibold"
                    : "border-gray-400"
                }`}
                onClick={() => handlePropertyTypeClick(type)}
              >
                {type}
              </button>
            ))}
          </div>
        </div>



        {/* Message */}
        <div className="flex items-end gap-5">
          <label className="text-sm w-20">Message</label>
          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            className="w-full border-b border-gray-400 focus:outline-none focus:border-black py-1"
          />
        </div>

        {/* Submit */}
        <div className="w-full text-center py-2">
          <button
            type="submit"
            className="bg-[#9A6E3A] text-white px-14 py-2.5 mt-2 mb-4"
            disabled={submitting}
          >
            {submitting ? "Please wait..." : "SUBMIT"}
          </button>
        </div>

        {/* Messages */}
        {error && <div className="text-red-600 text-base font-medium">{error}</div>}
        {success && <div className="text-green-600 text-base font-medium">{success}</div>}
      </form>
    </EnquiryModal>
  );
};

export default EnquiryModalPopup;
